import type { RJSFSchema } from '@rjsf/utils';
import type { FlowFormData, ResolveStepAutoFill, StepAutoFillResult } from './types';

export type NamedTemplateSelection = {
  id: string;
  name: string;
};

const PREFERRED_FIELD_KEYS = ['template', 'template_id', 'template_name', 'element_name'] as const;

export const collectStringEnums = (schema: RJSFSchema | undefined): Array<{ fieldKey: string; enumValues: string[] }> => {
  const properties = (schema?.properties ?? {}) as Record<string, { type?: unknown; enum?: unknown[] }>;
  const result: Array<{ fieldKey: string; enumValues: string[] }> = [];
  for (const [fieldKey, prop] of Object.entries(properties)) {
    if (!prop || !Array.isArray(prop.enum)) continue;
    const enumValues = prop.enum.filter((value): value is string => typeof value === 'string');
    if (enumValues.length > 0) result.push({ fieldKey, enumValues });
  }
  return result;
};

/** Match connector template enums shaped like `Name (id)`, bare name, or exact id — fail closed on ambiguous fuzzy matches. */
export const findNamedTemplateEnumValue = (enumValues: string[], selection: NamedTemplateSelection): string | undefined => {
  const { id, name } = selection;
  if (!id && !name) return undefined;

  if (name && id) {
    const exactWithId = `${name} (${id})`;
    if (enumValues.includes(exactWithId)) return exactWithId;
  }

  if (id) {
    if (enumValues.includes(id)) return id;

    const byParenId = enumValues.filter((value) => value.endsWith(` (${id})`));
    if (byParenId.length === 1) {
      const only = byParenId[0];
      // Unique `(id)` match is enough when name is unknown; if name is provided it must agree.
      if (!name || only.startsWith(`${name} (`)) return only;
      return undefined;
    }
    if (name && byParenId.length > 1) {
      const withName = byParenId.find((value) => value.startsWith(`${name} (`));
      if (withName) return withName;
    }
  }

  if (name) {
    if (enumValues.includes(name)) return name;
    const byNameWithId = enumValues.filter((value) => value.startsWith(`${name} (`));
    if (byNameWithId.length === 1) return byNameWithId[0];
  }

  return undefined;
};

const pickPreferredEnum = (enums: Array<{ fieldKey: string; enumValues: string[] }>) =>
  PREFERRED_FIELD_KEYS.map((key) => enums.find((entry) => entry.fieldKey === key)).find(Boolean) ?? enums[0];

/** Auto-fill + skip a template enum step for SMS/Email Edit or Delete Template flows. */
export const createNamedTemplateEditFlowHandler = (selection: NamedTemplateSelection | undefined): ResolveStepAutoFill => {
  return ({ formSchema }): StepAutoFillResult | null => {
    if (!selection?.id && !selection?.name) return null;

    const enums = collectStringEnums(formSchema.schema);
    if (enums.length === 0) return null;

    const preferred = pickPreferredEnum(enums);
    if (!preferred) return null;

    const matchedValue = findNamedTemplateEnumValue(preferred.enumValues, selection);
    if (!matchedValue) return null;

    const data: FlowFormData = { [preferred.fieldKey]: matchedValue };
    return { data, skip: true };
  };
};
